import { jsPDF } from 'jspdf';
import apiClient from '@/api/client';
import { SecureStorage } from '@/utils/secureStorage';

export function useTicketPrint() {
  const formatMoney = (amount) => {
    return Number(amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const loadImage = (url) => {
    return new Promise((resolve) => {
      if (!url) return resolve(null);
      const proxiedUrl = url.replace('https://frontline.therealdevs.pro', '/api-proxy');
      const img = new Image();
      img.setAttribute('crossOrigin', 'anonymous');
      img.onload = () => resolve(img);
      img.onerror = () => resolve(null);
      img.src = proxiedUrl;
    });
  };

  // Generador de ticket dinámico basado en las configuraciones de la empresa
  const generateTicket = async (paymentData) => {
    try {
      // 1. Traer datos de la empresa
      const configRes = await apiClient.get('/company_config');
      const config = Array.isArray(configRes.data) ? configRes.data[0] : configRes.data;
      const logoImg = await loadImage(config.logo_url);

      // --- LÓGICA INTELIGENTE DE VISIBILIDAD DE DESGLOSE ---
      // Evaluamos si el crédito es FLAT y consultamos la configuración respectiva.
      // Soportamos que el valor venga como booleano (true), numérico (1) o string ('1').
      const isFlat = paymentData.interest_type === 'FLAT';
      const showBreakdown = isFlat
        ? (config.print_flat_details === true || config.print_flat_details === 1 || config.print_flat_details === '1')
        : (config.print_normal_details === true || config.print_normal_details === 1 || config.print_normal_details === '1');

      // 2. Configurar el lienzo para Ticket Térmico (80mm de ancho)
      const doc = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: [80, 200] // Altura 200mm, la térmica corta sola
      });

      const pageWidth = 80;
      const margin = 5;
      let y = 5;

      // --- Helper para líneas punteadas estilo ticket ---
      const drawDashedLine = () => {
        doc.setLineDashPattern([1, 1], 0);
        doc.setLineWidth(0.3);
        doc.line(margin, y, pageWidth - margin, y);
        doc.setLineDashPattern([], 0); // Reset a línea sólida
        y += 4;
      };

      // --- Helper para filas de dos columnas ---
      const printRow = (label, value, isBold = false) => {
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.text(label, margin, y);
        doc.text(String(value), pageWidth - margin, y, { align: 'right' });
        y += 4;
      };

      // ==========================================
      // CABECERA DEL TICKET
      // ==========================================
      if (logoImg) {
        const imgWidth = 25;
        const imgHeight = (logoImg.height * imgWidth) / logoImg.width;
        doc.addImage(logoImg, 'PNG', (pageWidth - imgWidth) / 2, y, imgWidth, imgHeight);
        y += imgHeight + 4;
      }

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(11);
      doc.text(config.trade_name || 'NEXUS CREDIT', pageWidth / 2, y, { align: 'center' });
      y += 4;

      doc.setFontSize(7.5);
      doc.setFont('helvetica', 'normal');
      if (config.address) {
        const splitAddress = doc.splitTextToSize(config.address, pageWidth - 10);
        doc.text(splitAddress, pageWidth / 2, y, { align: 'center' });
        y += (splitAddress.length * 3) + 1;
      }
      if (config.phone) {
        doc.text(`Tel: ${config.phone}`, pageWidth / 2, y, { align: 'center' });
        y += 4;
      }

      y += 1;
      drawDashedLine();

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(9);
      doc.text('RECIBO DE PAGO', pageWidth / 2, y + 1, { align: 'center' });
      y += 4;

      drawDashedLine();

      // ==========================================
      // DATOS GENERALES DE LA TRANSACCIÓN
      // ==========================================
      doc.setFontSize(8);
      printRow('Fecha:', paymentData.payment_date || new Date().toLocaleString('es-ES'));
      printRow('Cajero:', paymentData.cashier_name || SecureStorage.getItem('llevo_user')?.full_name || 'SISTEMA');
      printRow('Transacción:', paymentData.transaction_id.substring(0, 13).toUpperCase() || 'PENDIENTE');
      printRow('Crédito No.:', paymentData.loan_code || 'N/D');
      y += 1;

      doc.setFont('helvetica', 'bold');
      doc.text('Cliente:', margin, y);
      doc.setFont('helvetica', 'normal');
      y += 4;
      const splitName = doc.splitTextToSize(paymentData.customer_name || 'N/D', pageWidth - 10);
      doc.text(splitName, margin, y);
      y += (splitName.length * 4) + 1;

      drawDashedLine();

      // ==========================================
      // FINANZAS: DESGLOSE (Decidido por la Config)
      // ==========================================
      if (showBreakdown) {
        printRow('Abono Capital:', `$ ${formatMoney(paymentData.principal_applied)}`);
        printRow('Intereses:', `$ ${formatMoney(paymentData.interest_applied)}`);

        if (Number(paymentData.late_fee_applied) > 0) {
          printRow('Mora/Cargos:', `$ ${formatMoney(paymentData.late_fee_applied)}`);
        }
        y += 2;
        drawDashedLine();
      }

      // ==========================================
      // TOTALES
      // ==========================================
      doc.setFontSize(11);
      printRow('TOTAL RECIBIDO:', `$ ${formatMoney(paymentData.amount_paid)}`, true);

      if (showBreakdown) {
        doc.setFontSize(8);
        y += 2;
        printRow('Nuevo Saldo:', `$ ${formatMoney(paymentData.new_balance)}`, true);
      }

      y += 2;
      drawDashedLine();

      // ==========================================
      // PIE DEL TICKET
      // ==========================================
      doc.setFontSize(8);
      doc.setFont('helvetica', 'italic');
      doc.text('¡Gracias por su pago!', pageWidth / 2, y, { align: 'center' });
      y += 4;
      doc.text('Conserve este recibo para cualquier reclamo.', pageWidth / 2, y, { align: 'center' });

      doc.autoPrint();

      // Abrimos el PDF en una pestaña nueva
      const pdfBlobUrl = doc.output('bloburl');
      window.open(pdfBlobUrl, '_blank');

    } catch (error) {
      console.error('Error generando ticket de pago:', error);
    }
  };

  return { generateTicket };
}